import { Injectable, OnDestroy } from '@angular/core';
import { Calendar, CalendarType } from '@vendasta/meetings';
import {
  combineLatest,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { GROUP, SERVICE } from '../../constants';
import { MeetingListSharedDataService } from '../shared/shared-table-view.service';
import { MeetingSchedulerStoreService } from '../../data-providers/meeting-scheduler-store.service';
import { TranslateService } from '@ngx-translate/core';
import { EventTypeSummary } from '@vendasta/meetings/lib/_internal/objects/meeting-host';
import { DurationPipe } from '@galaxy/crm/integrations/meeting-event/src/pipes';

@Injectable()
export class MeetingListLoadDataService implements OnDestroy {
  private unsubscribe$ = new Subject<void>();
  private allEventTypes$: Observable<EventTypeSummary[]>;
  personalCalendar$: Observable<Calendar>;
  groupCalendars$: Observable<Calendar[]>;
  allGroupCalendarIds$: Observable<string[]>;
  constructor(
    private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService,
    private readonly sharedDataService: MeetingListSharedDataService,
    private readonly translate: TranslateService,
  ) {}

  getDefaultGroup$(): Observable<any[]> {
    return combineLatest([this.personalCalendar$, this.groupCalendars$, this.allEventTypes$]).pipe(
      map(([personalCalendar, groupCalendars, eventTypes]) => {
        const personalEventTypes = eventTypes.filter((et) => et.calendarType === CalendarType.CALENDAR_TYPE_PERSONAL);
        const groupEventTypesByCalendar = this.groupEventTypesByCalendarId(eventTypes);

        const defaultGroups = [];

        if (personalEventTypes.length > 0) {
          defaultGroups.push({
            id: personalCalendar?.id,
            name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TABLE.VIEW.DEFAULT_PERSONAL_GROUP.NAME'),
            description: this.translate.instant(
              'MEETING_SCHEDULER.MEETING_LIST.TABLE.VIEW.DEFAULT_PERSONAL_GROUP.DESCRIPTION',
            ),
            isPinned: true,
            associations: personalEventTypes,
            bookingUrl: personalCalendar?.bookingUrl,
            isDefaultGroup: true,
          });
        }

        groupCalendars.forEach((calendar: Calendar) => {
          const groupEventTypes = groupEventTypesByCalendar[calendar?.id] || [];
          if (groupEventTypes.length > 0) {
            defaultGroups.push({
              id: calendar?.id,
              name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TABLE.VIEW.DEFAULT_TEAM_GROUP.NAME', {
                teamName: calendar?.displayName,
              }),
              description: this.translate.instant(
                'MEETING_SCHEDULER.MEETING_LIST.TABLE.VIEW.DEFAULT_TEAM_GROUP.DESCRIPTION',
                {
                  teamName: calendar?.displayName,
                },
              ),
              isPinned: true,
              associations: groupEventTypes,
              bookingUrl: calendar?.bookingUrl,
              isDefaultGroup: true,
            });
          }
        });

        return defaultGroups;
      }),
    );
  }

  private groupEventTypesByCalendarId(eventTypes: EventTypeSummary[]): { [calendarId: string]: EventTypeSummary[] } {
    const grouped: { [calendarId: string]: EventTypeSummary[] } = {};
    eventTypes
      .filter((et) => et.calendarType === CalendarType.CALENDAR_TYPE_GROUP)
      .forEach((et) => {
        if (!grouped[et.calendarId]) grouped[et.calendarId] = [];
        grouped[et.calendarId].push(et);
      });
    return grouped;
  }

  getEventTypeData() {
    this.personalCalendar$ = this.meetingSchedulerStoreService.loadPersonalCalendar();
    this.sharedDataService.setPersonalDefaultCalendar(this.personalCalendar$);

    this.groupCalendars$ = this.meetingSchedulerStoreService.loadGroupCalendars();
    this.sharedDataService.setGroupCalendar(this.groupCalendars$);

    this.allGroupCalendarIds$ = this.groupCalendars$.pipe(
      map((calendars) => calendars?.map((calendar) => calendar.id) || []),
      startWith([]),
    );

    this.allGroupCalendarIds$
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap((calendarIds) =>
          calendarIds.length
            ? forkJoin(
                calendarIds.map((calendarId) => {
                  return this.meetingSchedulerStoreService.getGroupCalendarDataFromState(calendarId)
                    ? of(null)
                    : this.meetingSchedulerStoreService.loadCalendar(calendarId);
                }),
              )
            : of([]),
        ),
      )
      .subscribe();

    this.allEventTypes$ = this.meetingSchedulerStoreService.loadAllEventTypes().pipe(shareReplay(1));

    return this.allEventTypes$.pipe(
      map((eventTypes) =>
        eventTypes.map((et) => ({
          ...et,
          meetingTypeSlug: et.slug,
          duration: DurationPipe.transformDuration(`${et.duration}s`),
          eventType:
            et.calendarType === CalendarType.CALENDAR_TYPE_PERSONAL
              ? this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.PERSONAL_EVENT')
              : et.calendarType === CalendarType.CALENDAR_TYPE_GROUP
                ? this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TEAM_EVENT')
                : this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.SESSION_EVENT'),
        })),
      ),
      shareReplay(1),
    );
  }

  loadGroupOrServiceData(selectedToggle): Observable<any[]> {
    let data$: Observable<any[]>;
    switch (selectedToggle) {
      case GROUP:
        data$ = this.meetingSchedulerStoreService.loadGroups();
        break;
      case SERVICE:
        data$ = this.meetingSchedulerStoreService.loadServices();
        break;
      default:
        data$ = this.getEventTypeData();
    }
    return this.sortData(data$, selectedToggle);
  }

  sortData(data$: Observable<any[]>, selectedToggle: string): Observable<any[]> {
    if (selectedToggle === GROUP) {
      return combineLatest([this.getDefaultGroup$(), data$]).pipe(
        map(([defaultGroups, groupData]: [any[], any[]]) => {
          const allData = [...defaultGroups, ...(Array.isArray(groupData) ? groupData : [])];
          return this.applySorting(allData);
        }),
      );
    }

    return data$.pipe(
      map((data) => {
        if (Array.isArray(data)) {
          return this.applySorting(data);
        }
        return [];
      }),
    );
  }

  private applySorting(data: any[]): any[] {
    return data.sort((a, b) => {
      if (a.isPinned !== b.isPinned) {
        return Number(b.isPinned) - Number(a.isPinned);
      }
      const nameA = a.name ? a.name.toLowerCase() : '';
      const nameB = b.name ? b.name.toLowerCase() : '';

      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
